import React, { memo, useCallback } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ConfirmDeleteButton from '../components/ConfirmDeleteButton';
import {
  ChevronRight,
  Clapperboard,
  File,
  Flag,
  Image,
  MessageSquare,
  Play,
  Smile,
  Sparkles,
  Video,
  Workflow
} from 'lucide-react';

/** Flow indigo. Bracket icon = array/collection on a bus; compact dot = single value / one item (e.g. one tool ref out). */
const FLOW = '#6366f1';
const DATA_IN = '#3b82f6';
const DATA_OUT = '#22c55e';

const ACCENT = {
  wfTrigger: 'rgb(34, 197, 94)',
  wfAction: 'rgb(99, 102, 241)',
  wfOutput: 'rgb(251, 113, 133)',
  wfTextGen: 'rgb(251, 191, 36)',
  wfToolRef: 'rgb(245, 158, 11)',
  telegramEvent: 'rgb(56, 189, 248)'
};

export const TEXT_GEN_MODELS = [
  { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { id: 'gpt-4o', label: 'GPT-4o' },
  { id: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
];

const TOOL_PIN = '#f59e0b';

const MUTED = 'rgba(161, 161, 170, 0.95)';

/** Brackets = collection / array (buildbleu semantics). Remix path 16×16. */
function PinBracketIcon({ color }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      className="nodrag"
      style={{ color, display: 'block' }}
      aria-hidden
    >
      <path fill="none" d="M0 0h24v24H0z" />
      <path fill="currentColor" d="M15 4v2h3v12h-3v2h5V4zM4 20h5v-2H6V6h3V4H4z" />
    </svg>
  );
}

/** Single element / one item — small solid dot (not the thick ring from older mock). */
function PinSingleDot({ color }) {
  return (
    <Box
      className="nodrag"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: color,
        pointerEvents: 'none',
        boxShadow: `0 0 0 1px ${color}cc`
      }}
    />
  );
}

function BleuNodeCard({ selected, ringColor, children }) {
  return (
    <Box
      className="wf-node-bleu wf-bleu-node-card"
      sx={{
        width: 256,
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        bgcolor: '#18181b',
        boxShadow: selected
          ? `0 4px 6px -1px rgba(0,0,0,0.26), 0 2px 4px -2px rgba(0,0,0,0.2), ${ringColor} 0 0 0 2px`
          : '0 4px 6px -1px rgba(0,0,0,0.26), 0 2px 4px -2px rgba(0,0,0,0.2)',
        outline: 'none',
        overflow: 'visible'
      }}
    >
      {children}
    </Box>
  );
}

/** p-2 border-b — dot w-3 h-3 rounded, h3 text-xs font-semibold, remove control. */
function BleuHeader({ dotColor, title, nodeId }) {
  const { deleteElements } = useReactFlow();

  const onRemove = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      void deleteElements({ nodes: [{ id: nodeId }] });
    },
    [nodeId, deleteElements]
  );

  return (
    <Box
      sx={{
        p: 1,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, mr: 2 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '4px', bgcolor: dotColor, flexShrink: 0 }} />
          <Typography
            component="h3"
            sx={{
              fontSize: '0.75rem',
              lineHeight: '12px',
              fontWeight: 600,
              color: '#fafafa',
              m: 0,
              cursor: 'default',
              userSelect: 'none'
            }}
            noWrap
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConfirmDeleteButton
            onConfirm={onRemove}
            iconSize={13}
            className="nodrag nopan"
            sx={{
              color: MUTED,
              '&:hover': {
                color: '#fca5a5',
                bgcolor: 'rgba(248, 113, 113, 0.12)'
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

const handleHitSx = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  transform: 'none',
  border: 'none',
  background: 'transparent',
  opacity: 1
};

/** Left target: pl-4 row, handle box w-5 h-5 at left -4px (Bleu). */
function PinTargetLeft({ id, label, visual }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 24,
        pl: 2
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: -4,
          transform: 'translateY(-50%)',
          width: 20,
          height: 20
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id={id}
          className="wf-bleu-handle wf-bleu-handle-left nodrag nopan"
          style={handleHitSx}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16
          }}
        >
          <Box sx={{ position: 'relative', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {visual}
          </Box>
        </Box>
      </Box>
      <Typography
        component="span"
        className="nodrag"
        sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, lineHeight: 1.25 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/** Right source: pr-4 row, handle box at right -4px. */
function PinSourceRight({ id, label, visual }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'flex-end',
        minHeight: 24,
        pr: 2
      }}
    >
      <Typography
        component="span"
        className="nodrag"
        sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, lineHeight: 1.25 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: -4,
          transform: 'translateY(-50%)',
          width: 20,
          height: 20
        }}
      >
        <Handle
          type="source"
          position={Position.Right}
          id={id}
          className="wf-bleu-handle wf-bleu-handle-right nodrag nopan"
          style={handleHitSx}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16
          }}
        >
          <Box sx={{ position: 'relative', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {visual}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PinRowBleu({ left, right }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>{left}</Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>{right}</Box>
    </Box>
  );
}

function BleuDivider() {
  return <Box component="hr" sx={{ border: 'none', borderTop: '1px solid rgba(255, 255, 255, 0.1)', my: 1, mx: 0 }} />;
}

function WfTriggerNode({ id, data, selected }) {
  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.wfTrigger}>
      <BleuHeader dotColor="#22c55e" title={data.label || 'Start'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={
            <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
              {data.hint || 'Entry point'}
            </Typography>
          }
          right={
            <PinSourceRight
              id="flow-out"
              label="Flow"
              visual={<PinSingleDot color={FLOW} />}
            />
          }
        />
      </Box>
    </BleuNodeCard>
  );
}

function WfActionNode({ id, data, selected }) {
  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.wfAction}>
      <BleuHeader dotColor={FLOW} title={data.label || 'Step'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={
            <PinTargetLeft
              id="flow-in"
              label="Flow"
              visual={<PinSingleDot color={FLOW} />}
            />
          }
          right={
            <PinSourceRight id="flow-out" label="Flow" visual={<PinSingleDot color={FLOW} />} />
          }
        />
        <PinRowBleu
          left={
            <PinTargetLeft
              id="data-in"
              label="Data"
              visual={<PinBracketIcon color={DATA_IN} />}
            />
          }
          right={
            <PinSourceRight
              id="data-out"
              label="Data"
              visual={<PinBracketIcon color={DATA_OUT} />}
            />
          }
        />
        <BleuDivider />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.5, pointerEvents: 'none' }}>
          <Box>
            <Typography
              component="label"
              className="nodrag"
              sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, mb: 0.5, display: 'block' }}
            >
              Worker
            </Typography>
            <Button
              fullWidth
              disabled
              size="small"
              variant="outlined"
              className="nodrag"
              tabIndex={-1}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 400,
                py: 0.5,
                px: 1,
                minHeight: 32,
                borderColor: 'rgba(255,255,255,0.15)',
                color: 'text.secondary',
                bgcolor: 'rgba(255,255,255,0.03)'
              }}
            >
              <Typography noWrap sx={{ fontSize: '0.75rem' }}>
                {data.hint || 'Configure on fork'}
              </Typography>
            </Button>
          </Box>
        </Box>
        <BleuDivider />
        <Button
          fullWidth
          disabled
          size="small"
          className="nodrag"
          tabIndex={-1}
          endIcon={<ChevronRight size={14} strokeWidth={2} aria-hidden />}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            color: MUTED,
            py: 0.25,
            opacity: 0.5
          }}
        >
          Advanced settings
        </Button>
      </Box>
    </BleuNodeCard>
  );
}

function WfTelegramEventNode({ id, data, selected }) {
  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.telegramEvent}>
      <BleuHeader dotColor={ACCENT.telegramEvent} title={data.label || 'Telegram'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={
            <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
              {data.hint || 'Inbound event'}
            </Typography>
          }
          right={
            <PinSourceRight id="flow-out" label="Flow" visual={<PinSingleDot color={FLOW} />} />
          }
        />
      </Box>
    </BleuNodeCard>
  );
}

function WfTextGenNode({ id, data, selected }) {
  const { setNodes } = useReactFlow();
  const model = data.model || 'gpt-4o-mini';

  const onModelChange = useCallback(
    (e) => {
      const v = e.target.value;
      setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, model: v } } : n)));
    },
    [id, setNodes]
  );

  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.wfTextGen}>
      <BleuHeader dotColor="#fbbf24" title={data.label || 'Text Generation'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={<PinTargetLeft id="flow-in" label="Flow" visual={<PinSingleDot color={FLOW} />} />}
          right={<PinSourceRight id="flow-out" label="Flow" visual={<PinSingleDot color={FLOW} />} />}
        />
        <PinRowBleu
          left={
            <PinTargetLeft
              id="data-prompt"
              label="Prompt"
              visual={<PinSingleDot color={DATA_IN} />}
            />
          }
          right={
            <Box sx={{ minHeight: 24, pr: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Typography className="nodrag" variant="caption" sx={{ color: MUTED, fontSize: '0.65rem' }}>
                LLM
              </Typography>
            </Box>
          }
        />
        <PinRowBleu
          left={
            <PinTargetLeft
              id="tools-in"
              label="Tools"
              visual={<PinBracketIcon color={TOOL_PIN} />}
            />
          }
          right={<Box sx={{ minHeight: 24, width: 8 }} />}
        />
        <BleuDivider />
        <FormControl size="small" fullWidth className="nodrag nopan">
          <InputLabel id={`${id}-model`}>Model</InputLabel>
          <Select
            labelId={`${id}-model`}
            label="Model"
            value={model}
            onChange={onModelChange}
            sx={{ fontSize: '0.75rem' }}
          >
            {TEXT_GEN_MODELS.map((m) => (
              <MenuItem key={m.id} value={m.id} dense>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </BleuNodeCard>
  );
}

function WfToolRefNode({ id, data, selected }) {
  const toolId = data.toolId != null ? String(data.toolId) : '';
  const desc = data.description != null ? String(data.description).trim() : '';
  const hint = data.hint != null ? String(data.hint).trim() : '';
  const subline =
    desc || (hint && hint !== toolId ? hint : '') || 'Registered AI tool';

  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.wfToolRef}>
      <BleuHeader dotColor={TOOL_PIN} title={data.label || 'Tool'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={
            <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, pl: 2 }}>
              {subline}
            </Typography>
          }
          right={
            <PinSourceRight
              id="tool-out"
              label="Tool"
              visual={<PinSingleDot color={TOOL_PIN} />}
            />
          }
        />
      </Box>
    </BleuNodeCard>
  );
}

function WfOutputNode({ id, data, selected }) {
  return (
    <BleuNodeCard selected={selected} ringColor={ACCENT.wfOutput}>
      <BleuHeader dotColor="#fb7185" title={data.label || 'End'} nodeId={id} />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PinRowBleu
          left={<PinTargetLeft id="flow-in" label="Flow" visual={<PinSingleDot color={FLOW} />} />}
          right={
            <Box sx={{ minHeight: 24, pr: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Typography className="nodrag" sx={{ fontSize: '0.75rem', fontWeight: 500, color: MUTED, textAlign: 'right' }}>
                {data.hint || 'Completes run'}
              </Typography>
            </Box>
          }
        />
        <PinRowBleu
          left={
            <PinTargetLeft id="data-in" label="Data" visual={<PinSingleDot color={DATA_IN} />} />
          }
          right={
            <Box
              sx={{
                height: 20,
                minHeight: 24,
                pr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            />
          }
        />
      </Box>
    </BleuNodeCard>
  );
}

const MemoTelegramEvent = memo(WfTelegramEventNode);

export const wfNodeTypes = {
  wfTrigger: memo(WfTriggerNode),
  wfAction: memo(WfActionNode),
  wfOutput: memo(WfOutputNode),
  wfTextGen: memo(WfTextGenNode),
  wfToolRef: memo(WfToolRefNode),
  wfEventTgText: MemoTelegramEvent,
  wfEventTgPhoto: MemoTelegramEvent,
  wfEventTgVideo: MemoTelegramEvent,
  wfEventTgFile: MemoTelegramEvent,
  wfEventTgSticker: MemoTelegramEvent,
  wfEventTgAnimation: MemoTelegramEvent
};

/** Visual metadata for event nodes (palette + context menu). */
export const EVENT_PALETTE_META = {
  wfTrigger: { Icon: Play, iconColor: '#4ade80', iconWellBg: 'rgba(74, 222, 128, 0.14)' },
  wfEventTgText: { Icon: MessageSquare, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' },
  wfEventTgPhoto: { Icon: Image, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' },
  wfEventTgVideo: { Icon: Video, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' },
  wfEventTgFile: { Icon: File, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' },
  wfEventTgSticker: { Icon: Smile, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' },
  wfEventTgAnimation: { Icon: Clapperboard, iconColor: '#38bdf8', iconWellBg: 'rgba(56, 189, 248, 0.14)' }
};

/** Logic nodes section (not events, not registered tools). */
export const PALETTE_NODE_TYPES = [
  {
    type: 'wfAction',
    title: 'Step',
    Icon: Workflow,
    iconColor: '#a5b4fc',
    iconWellBg: 'rgba(165, 180, 252, 0.14)'
  },
  {
    type: 'wfOutput',
    title: 'Output',
    Icon: Flag,
    iconColor: '#fb7185',
    iconWellBg: 'rgba(251, 113, 133, 0.14)'
  },
  {
    type: 'wfTextGen',
    title: 'Text Generation',
    Icon: Sparkles,
    iconColor: '#fbbf24',
    iconWellBg: 'rgba(251, 191, 36, 0.16)'
  }
];
